import { LogOut, PanelLeft, Sparkles } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { paths } from '@/app/paths';
import { BRAND, BrandLogo } from '@/components/Brand';
import { cn } from '@/design-system/lib/cn';
import { Avatar } from '@/design-system/ui/Avatar';
import { PERSONA_LABEL, useStore } from '@/store/AppStore';

import NavGroupMenu from './NavGroupMenu';
import { isActive, isGroup, navItems } from './navigation';
import OrgSwitcher from './OrgSwitcher';

type SidebarProps = {
  collapsed: boolean;
  onToggleCollapse: () => void;
  /** No celular a sidebar é uma gaveta sobre o conteúdo. */
  mobile: boolean;
  open: boolean;
  onNavigate: () => void;
  onLogout: () => void;
};

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  mobile,
  open,
  onNavigate,
  onLogout,
}: SidebarProps) {
  const { pathname } = useLocation();
  const { permissions, me, persona, guest } = useStore();
  const labels = mobile || !collapsed;
  const items = navItems(permissions, { agenda: !guest });

  return (
    <aside
      aria-label="Navegação principal"
      inert={mobile && !open ? true : undefined}
      className={cn(
        'top-0 left-0 z-50 flex h-dvh shrink-0 flex-col self-start bg-sidebar text-sidebar-ink',
        mobile
          ? 'fixed w-72 transition-[translate,box-shadow] duration-300 ease-drawer will-change-[translate]'
          : 'sticky transition-[width] duration-200',
        !mobile && (collapsed ? 'w-19' : 'w-72'),
        mobile && (open ? 'shadow-drawer' : '-translate-x-full')
      )}
    >
      <div className={cn('flex h-16 shrink-0 items-center gap-2.5 px-4.5', !labels && 'justify-center')}>
        <BrandLogo tone="dark" decorative={labels} className="h-9 w-auto" />
        {labels ? (
          <span className="flex min-w-0 flex-col gap-0.5 leading-[1.05]">
            <span className="text-title-sm font-extrabold tracking-[-0.025em] whitespace-nowrap text-white">
              {BRAND.name}
            </span>
            <span className="truncate text-micro font-bold tracking-wider text-primary-hover-line uppercase">
              {BRAND.subtitle}
            </span>
          </span>
        ) : null}
      </div>

      <OrgSwitcher labels={labels} onNavigate={onNavigate} />

      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain px-2.5 py-2">
        {items.map((item) => {
          if (isGroup(item)) {
            return (
              <NavGroupMenu
                key={item.label}
                group={item}
                labels={labels}
                mobile={mobile}
                onNavigate={onNavigate}
              />
            );
          }

          const active = isActive(item, pathname);
          const Icon = item.icon;

          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              aria-current={active ? 'page' : undefined}
              title={labels ? undefined : item.label}
              className={cn(
                'relative flex h-10.5 shrink-0 items-center gap-3 rounded-control px-3.5 text-body no-underline transition-colors',
                !labels && 'justify-center',
                active
                  ? 'bg-sidebar-active font-semibold text-white before:absolute before:inset-y-2.5 before:left-0 before:w-0.75 before:rounded-pill before:bg-primary-focus'
                  : 'font-medium text-sidebar-ink hover:bg-sidebar-hover hover:text-white'
              )}
            >
              <Icon aria-hidden className="size-4.5 shrink-0 stroke-[1.8]" />
              {labels ? <span>{item.label}</span> : <span className="sr-only">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {labels && permissions.generate ? (
        <div className="mx-3.5 mb-4 flex shrink-0 flex-col gap-2.5 rounded-block border border-sidebar-card-line bg-sidebar-card p-4 max-lg:hidden max-md:flex max-md:p-2.5">
          <p className="m-0 flex items-center gap-2 text-small font-semibold text-white max-md:hidden">
            <Sparkles aria-hidden className="size-4 stroke-[1.8] text-sidebar-accent" />
            Geração inteligente
          </p>
          <p className="m-0 text-small text-sidebar-ink max-md:hidden">
            Crie escalas completas com poucos cliques usando regras e otimização.
          </p>
          <Link
            to={paths.generate}
            onClick={onNavigate}
            className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-control bg-ai text-control font-semibold text-white no-underline hover:text-white hover:brightness-110"
          >
            <Sparkles aria-hidden className="size-4 stroke-[1.8]" />
            Gerar escala
          </Link>
        </div>
      ) : null}

      <div className="flex shrink-0 flex-col gap-1 border-t border-sidebar-line px-2.5 py-3">
        <div className={cn('flex items-center gap-2.5 px-1.5 py-1', !labels && 'justify-center')}>
          <Avatar name={me.name} photo={me.photo} size="lg" />
          {labels ? (
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-control font-semibold text-white">{me.name}</p>
              <p className="m-0 text-caption text-sidebar-muted">{PERSONA_LABEL[persona]}</p>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onLogout}
          title="Sair"
          className={cn(
            'flex h-9 items-center gap-2.5 rounded-control-sm px-3 text-small text-sidebar-ink hover:bg-sidebar-hover',
            !labels && 'justify-center'
          )}
        >
          <LogOut aria-hidden className="size-4 stroke-[1.8]" />
          {labels ? 'Sair' : <span className="sr-only">Sair</span>}
        </button>
        {!mobile ? (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            className={cn(
              'flex h-9 items-center gap-2.5 rounded-control-sm px-3 text-small text-sidebar-muted hover:bg-sidebar-hover',
              !labels && 'justify-center'
            )}
          >
            <PanelLeft aria-hidden className="size-4 stroke-[1.8]" />
            {labels ? <span aria-hidden>Recolher menu</span> : null}
          </button>
        ) : null}
      </div>
    </aside>
  );
}
